<template>
  <v-dialog v-model="editDialog" persistent max-width="600px">
    <v-card>
      <form @submit.prevent="validateBeforeSubmit($event)">
        <v-layout row class="no-select" style="background-color:#09132f">
          <h3 id="dialog-title" class="text-uppercase px-3 pt-3" style="text-xs-center letter-spacing:2px">
            <v-layout align-center>
              <v-icon light x-large class="rgba(0,0,0,0.3)--text pb-1">attach_money</v-icon>
              <span class="px-3">{{ $t(`Edit Bank Transaction's Information`) }}</span>
            </v-layout>
          </h3>
          <v-spacer />
          <v-card-actions>
            <v-btn fab icon dark @click="$emit('hidemodal')">
              <v-icon>close</v-icon>
            </v-btn>
          </v-card-actions>
        </v-layout>

        <v-card-title class="grey lighten-3">
          <v-layout column>
            <v-layout row wrap>
              <v-flex xs6 pa-2>
                <v-text-field
                  :value="viewInfo.balance_before_transaction"
                  :label="`${$t('Balance Before Transaction')} *`"
                  name="balance_before_transaction"
                  box
                  hide-details
                  readonly
                  background-color="white"
                  :error="errors.has('balance_before_transaction')"
                  v-validate="'required'"
                />
              </v-flex>

              <v-flex xs6 pa-2>
                <v-menu v-model="dateMenu" full-width max-width="290" :close-on-content-click="false">
                  <v-text-field
                    slot="activator"
                    :value="transaction_date"
                    :label="`${$t('Date')} *`"
                    prepend-inner-icon="event"
                    box
                    readonly
                    hide-details
                    name="transaction_date"
                    background-color="white"
                    autocomplete="off"
                    :error="errors.has('transaction_date')"
                    v-validate="'required|date_format:yyyy-MM-dd'"
                  />
                  <v-date-picker @change="dateMenu = false" v-model="transaction_date" />
                </v-menu>
              </v-flex>

              <v-flex xs11 :class="{ xs12: !hasPermissions(['users_store']) }" pa-2>
                <v-autocomplete
                  :items="fullStaffList"
                  v-model="viewInfo.depositor_id_fk"
                  :label="`${$t('Depositor Name')} *`"
                  box
                  hide-details
                  :error="errors.has('depositor_id_fk')"
                  background-color="white"
                  v-validate="'required'"
                  data-vv-name="depositor_id_fk"
                  :menu-props="{ transition: 'slide-y-transition' }"
                />
              </v-flex>

              <v-flex xs1 pt-2 v-if="hasPermissions(['users_store'])">
                <v-tooltip top>
                  <v-btn label @click="$emit('add-staff')" slot="activator" icon fab small color="green">
                    <v-icon color="white" size="25">add</v-icon>
                  </v-btn>
                  <span>{{ $t('Add new staff') }}</span>
                </v-tooltip>
              </v-flex>

              <v-flex xs12 pa-2>
                <p>{{ `${$t('Transaction Type')} *` }}</p>
                <v-radio-group
                  v-model="viewInfo.transaction_type"
                  name="transaction_type"
                  v-validate="'required'"
                  row
                  @change="newBalance"
                >
                  <v-radio :label="$t('Withdraw')" value="Withdraw" color="blue" />
                  <v-radio :label="$t('Deposit')" value="Deposit" color="blue" />
                </v-radio-group>
              </v-flex>

              <v-flex xs6 class="pa-2">
                <v-text-field
                  v-model="viewInfo.amount"
                  :label="`${$t('Amount')} *`"
                  name="amount"
                  box
                  hide-details
                  background-color="white"
                  :error="errors.has('amount')"
                  v-validate="'required|decimal|min_value:0'"
                  @input="newBalance"
                />
              </v-flex>

              <v-flex xs6 class="pa-2">
                <v-text-field
                  v-model="viewInfo.balance_after_transaction"
                  :label="`${$t('Balance After Transaction')} *`"
                  name="balance_after_transaction"
                  box
                  hide-details
                  readonly
                  background-color="white"
                  :error="errors.has('balance_after_transaction')"
                  v-validate="'required'"
                />
              </v-flex>
            </v-layout>
          </v-layout>
        </v-card-title>

        <v-divider light />

        <v-card-actions class="grey lighten-3">
          <v-layout column>
            <v-flex xs12 class="pb-2 px-2" style="color: #444;"
              >- {{ $t('Fields marked with asterisk ( * ) are required.') }}</v-flex
            >

            <v-layout justify-end>
              <v-btn outline color="primary" @click="$emit('hidemodal')">{{ $t('Cancel') }}</v-btn>
              <v-btn type="submit" color="success" :loading="btnUpdateLoading">
                <v-icon :right="$vuetify.rtl" :left="!$vuetify.rtl">save</v-icon>
                {{ $t('Update') }}
              </v-btn>
            </v-layout>
          </v-layout>
        </v-card-actions>
      </form>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import * as helpers from '@/mixins/helperFunctions.js'

export default {
  name: 'edit-component',

  props: {
    editDialog: { type: Boolean, required: true }
  },

  data() {
    return {
      dateMenu: false,
      btnUpdateLoading: false,
      transaction_date: ''
    }
  },

  computed: {
    ...mapGetters({
      viewInfo: 'bankTransactions/getTransactionInfo',
      fullStaffList: 'staff/getFullStaffsList'
    })
  },

  watch: {
    editDialog: {
      immediate: false,
      handler() {
        if (this.editDialog) {
          this.transaction_date = this.viewInfo.transaction_date
          this.addEscKeyListener()
        }
      }
    }
  },

  methods: {
    ...mapActions({
      updateBankInfo: 'bankTransactions/updateTransactionInfo'
    }),

    hasPermissions(permissions) {
      return helpers.userHasPermissions(permissions)
    },

    addEscKeyListener() {
      document.addEventListener('keyup', e => {
        // if the "ESC" button was pressed and this dialog is shown, hide it
        if (e.keyCode === 27 && this.editDialog) {
          this.$emit('hidemodal')
        }
      })
    },

    newBalance() {
      const amount = this.viewInfo.transaction_type === 'Withdraw' ? -this.viewInfo.amount : this.viewInfo.amount
      this.viewInfo.balance_after_transaction = Number(amount) + this.viewInfo.balance_before_transaction
    },

    validateBeforeSubmit(event) {
      this.$validator.validateAll().then(() => {
        if (!this.errors.any()) {
          this.btnUpdateLoading = true

          let formData = new FormData(event.target)
          formData.append('bank_id_fk', this.viewInfo.bank_id_fk)
          formData.append('depositor_id_fk', this.viewInfo.depositor_id_fk)

          // for (let pair of formData.entries()) {
          //   console.warn(`${pair[0]}: ${pair[1]}`)
          // }

          let payload = { id: this.viewInfo.bank_transaction_id, formData }

          this.updateBankInfo(payload)
            .then(() => {
              this.btnUpdateLoading = false
              this.$emit('updated')
            })
            .catch(() => {
              this.btnUpdateLoading = false
            })
        }
      })
    }
  }
}
</script>

<style scoped />
